import { Link } from 'react-router-dom'
import { BarChart3, FileCheck2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/utils'
import type { AnnouncementLink } from '@riverpark/types'

const POLL_STATUS_BADGE = {
  upcoming: { variant: 'pending', label: 'Upcoming' },
  open: { variant: 'paid', label: 'Open for voting' },
  closed: { variant: 'draft', label: 'Closed' },
} as const

/** basePath is the announcement section's own base (e.g. "/admin/announcements") — polls and
 * resolutions live as siblings under the same role prefix. */
export function AnnouncementLinkCard({
  link,
  basePath,
}: {
  link: AnnouncementLink
  basePath: string
}) {
  const sectionPrefix = basePath.replace(/\/announcements$/, '')

  if (link.entityType === 'poll') {
    const status = link.pollStatus ?? 'closed'
    const badge = POLL_STATUS_BADGE[status]
    const totalVotes = link.pollTotalVotes ?? 0

    return (
      <Link
        to={`${sectionPrefix}/polls/${link.entityId}`}
        className="block rounded border border-border-2 bg-card p-3 transition-colors hover:border-primary"
      >
        <div className="flex items-center gap-2">
          <BarChart3 size={15} className="shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate font-sans text-sm font-medium text-ink">
            {link.title}
          </span>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>
        {status === 'closed' && link.pollOptions && (
          <div className="mt-2.5 space-y-1.5">
            {link.pollOptions.map((option) => {
              const pct =
                totalVotes > 0 ? Math.round(((option.voteCount ?? 0) / totalVotes) * 100) : 0
              return (
                <div key={option.id} className="space-y-0.5">
                  <div className="flex items-center justify-between font-sans text-xs text-ink">
                    <span className="truncate">{option.label}</span>
                    <span className="shrink-0 text-muted-foreground">
                      {option.voteCount ?? 0} · {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-cream-2">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
            <p className="font-sans text-xs text-muted-foreground">{totalVotes} total votes</p>
          </div>
        )}
        {status !== 'closed' && (
          <p className="mt-1.5 font-sans text-xs text-primary">
            {status === 'open' ? 'Vote now →' : 'View poll →'}
          </p>
        )}
      </Link>
    )
  }

  return (
    <Link
      to={`${sectionPrefix}/resolutions/${link.entityId}`}
      className="block rounded border border-border-2 bg-card p-3 transition-colors hover:border-primary"
    >
      <div className="flex items-center gap-2">
        <FileCheck2 size={15} className="shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate font-sans text-sm font-medium text-ink">
          {link.title}
        </span>
        {link.resolutionPassedAt && (
          <span className="shrink-0 font-sans text-xs text-muted-foreground">
            {formatDate(new Date(link.resolutionPassedAt * 1000))}
          </span>
        )}
      </div>
      {link.resolutionExcerpt && (
        <p className="mt-1.5 line-clamp-2 font-sans text-xs text-muted-foreground">
          {link.resolutionExcerpt}
        </p>
      )}
    </Link>
  )
}
